import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Switch, TextField, Typography } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhoneIcon from '@mui/icons-material/Phone';

import { useAuth } from '@/components/auth/AuthProvider';
import { useDirection } from '@/hooks/useDirection';
import { semanticColors } from '@/theme/tokens';
import { TOGGLE_SX } from '@/ui-library/switch.styles';
import { ltrValueProps } from '@/utils/bidi';
import SaveStatus, { type SaveState } from './SaveStatus';
import SectionSaveRow from './SectionSaveRow';
import { SettingsRow, SettingsSection, useSettingsSurface } from './SettingsSection';
import { FIELD_LABEL_SX, INPUT_SX } from './settingsStyles';
import type { SettingsPanelProps } from './settingsTypes';

interface NotificationToggleProps {
  label: string;
  description: ReactNode;
  descriptionId: string;
  icon: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  state: SaveState;
  error?: string | null;
  divider?: boolean;
}

function NotificationToggle({
  label,
  description,
  descriptionId,
  icon,
  checked,
  onChange,
  state,
  error,
  divider,
}: NotificationToggleProps) {
  const { t } = useTranslation('settings');
  const surface = useSettingsSurface();
  const control = (
    <Switch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      disabled={state === 'saving'}
      sx={TOGGLE_SX}
      slotProps={{ input: { 'aria-label': label, 'aria-describedby': descriptionId } }}
    />
  );
  const status = (
    <SaveStatus state={state} error={error} savingLabel={t('saving')} savedLabel={t('saved')} />
  );
  if (surface === 'dialog') {
    return (
      <SettingsRow
        label={label}
        description={description}
        descriptionId={descriptionId}
        controlAlignment="start"
        divider={divider}
        control={control}
        status={status}
      />
    );
  }
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          <Box sx={{ color: semanticColors.textTertiary, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{label}</Typography>
            <Typography
              id={descriptionId}
              sx={{ fontSize: '13px', color: semanticColors.textTertiary }}
            >
              {description}
            </Typography>
          </Box>
        </Box>
        {control}
      </Box>
      {status}
    </>
  );
}

export default function NotificationSettingsSection({ settings }: SettingsPanelProps) {
  const { t } = useTranslation('settings');
  const surface = useSettingsSurface();
  const { user } = useAuth();
  const direction = useDirection();
  const {
    phone,
    savedPhone,
    savedPhoneConsent,
    phoneError,
    emailConsent,
    phoneConsent,
    groups,
    toggleEmailConsent,
    togglePhoneConsent,
    handlePhoneChange,
    handlePhoneKeyDown,
    savePhone,
    cancelPhone,
  } = settings;

  const hasSavedSmsRecipient =
    savedPhoneConsent && /^1?\d{10}$/.test((savedPhone || '').replace(/\D/g, ''));
  const recipient = (label: string, value: string) => (
    <Box component="span" sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: 0.75 }}>
      <Box component="span">{label}</Box>
      <Box
        component="span"
        {...ltrValueProps(direction)}
        sx={{ minWidth: 0, overflowWrap: 'anywhere', color: semanticColors.textPrimary }}
      >
        {value}
      </Box>
    </Box>
  );

  const onPhoneKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && groups.phone.canSave && groups.phone.state !== 'saving') {
      event.preventDefault();
      void savePhone();
      return;
    }
    handlePhoneKeyDown(event);
  };

  return (
    <SettingsSection>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        {surface === 'page' && (
          <CampaignIcon sx={{ color: semanticColors.textPrimary, mt: 0.2, fontSize: 20 }} />
        )}
        <Box>
          {surface === 'page' && (
            <Typography component="h2" sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}>
              {t('notifications')}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: surface === 'dialog' ? '14px' : '13px',
              color: semanticColors.textTertiary,
              mt: surface === 'page' ? 0.5 : 0,
            }}
          >
            {t('notificationsDesc')}
          </Typography>
        </Box>
      </Box>

      <NotificationToggle
        label={t('emailNotifications')}
        description={
          <>
            {t('emailNotificationsDesc')}
            <br />
            {t('unsubscribeAnytime')}
            {surface === 'dialog' &&
              user?.email &&
              recipient(t('notificationEmailRecipient'), user.email)}
          </>
        }
        descriptionId="settings-email-desc"
        icon={<EmailIcon sx={{ fontSize: 18 }} />}
        checked={emailConsent}
        onChange={toggleEmailConsent}
        state={groups.emailConsent.state}
        error={groups.emailConsent.error}
      />

      {surface === 'page' && <Divider sx={{ opacity: 0.4 }} />}

      <NotificationToggle
        label={t('textNotifications')}
        description={
          <>
            {t('smsNotificationsDesc')}
            <br />
            {t('replyStopToUnsubscribe')}
            {surface === 'dialog' &&
              phoneConsent &&
              (hasSavedSmsRecipient ? (
                <>
                  {recipient(t('notificationPhoneRecipient'), savedPhone)}
                  {phone !== savedPhone && (
                    <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                      {t('notificationPhoneDraft')}
                    </Box>
                  )}
                </>
              ) : (
                <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                  {t('notificationSmsPending')}
                </Box>
              ))}
          </>
        }
        descriptionId="settings-sms-desc"
        icon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
        checked={phoneConsent}
        onChange={togglePhoneConsent}
        state={groups.phoneConsent.state}
        error={groups.phoneConsent.error}
        divider={false}
      />

      {phoneConsent && (
        <Box
          sx={{
            pt: 1.5,
            mt: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, color: semanticColors.textTertiary }} />
            <Typography
              component="label"
              htmlFor="settings-phone-number"
              sx={{ ...FIELD_LABEL_SX, mb: 0 }}
            >
              {t('phoneNumber')}
            </Typography>
          </Box>
          <TextField
            id="settings-phone-number"
            value={phone}
            onChange={(event) => handlePhoneChange(event.target.value)}
            placeholder={t('phoneNumberPlaceholder')}
            size="small"
            type="tel"
            error={!!phoneError}
            helperText={phoneError}
            slotProps={{
              htmlInput: {
                onKeyDown: onPhoneKeyDown,
                autoComplete: 'tel',
                inputMode: 'tel',
                dir: 'ltr',
              },
            }}
            sx={{ width: '100%', ...INPUT_SX }}
          />
          <SectionSaveRow
            dirty={groups.phone.dirty}
            state={groups.phone.state}
            error={groups.phone.error}
            canSave={groups.phone.canSave}
            saveLabel={t('savePhone')}
            cancelLabel={t('cancel')}
            savingLabel={t('saving')}
            savedLabel={t('saved')}
            blockedHint={t('addPhoneToEnable')}
            onSave={() => void savePhone()}
            onCancel={cancelPhone}
          />
        </Box>
      )}
    </SettingsSection>
  );
}
